import { useEffect, useState } from "react";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { PageHeader } from "../../components/PageHeader";
import { Sheet } from "../../components/Sheet";
import { Toast } from "../../components/Toast";
import {
  BanknoteIcon,
  ArrowDownIcon,
  ArrowLeftRightIcon,
  ArrowUpIcon,
  CheckIcon,
  CreditCardIcon,
  PencilIcon,
  PlusIcon,
  UserIcon,
} from "../../components/icons";
import {
  useCreatePaymentMethod,
  usePaymentMethods,
  useReorderPaymentMethods,
  useUpdatePaymentMethod,
} from "./hooks";
import type { PaymentMethod, PaymentMethodInput } from "./api";

type Target = PaymentMethod | "new" | null;

const colorOptions: Array<{ value: PaymentMethodInput["color"]; label: string }> = [
  { value: "cash", label: "Efectivo (verde)" },
  { value: "transfer", label: "Transferencia (azul)" },
  { value: "card", label: "Tarjeta (violeta)" },
  { value: "other", label: "Otro (neutro)" },
  { value: "magenta", label: "Fucsia" },
];

const iconOptions: Array<{ value: PaymentMethodInput["icon"]; label: string }> = [
  { value: "Banknote", label: "Billete" },
  { value: "ArrowLeftRight", label: "Transferencia" },
  { value: "CreditCard", label: "Tarjeta" },
  { value: "UserCircle", label: "Cliente / cuenta" },
];

export function AdminPaymentMethodsView() {
  const [editing, setEditing] = useState<Target>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const methods = usePaymentMethods(true, true);
  const update = useUpdatePaymentMethod();
  const reorder = useReorderPaymentMethods();
  // Los nuevos medios caen al final: mayor displayOrder existente + 1.
  const nextDisplayOrder = (methods.data ?? []).reduce((max, method) => Math.max(max, method.displayOrder), -1) + 1;

  // Intercambia el medio de la fila `index` con su vecino inmediato y manda
  // la lista COMPLETA (en el orden ya mostrado) al endpoint de reorder. El
  // backend rechaza listas parciales con incomplete_order a propósito.
  function moveMethod(index: number, direction: -1 | 1) {
    const list = methods.data;
    if (!list) return;
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= list.length) return;
    const ids = list.map((method) => method.id);
    [ids[index], ids[targetIndex]] = [ids[targetIndex], ids[index]];
    reorder.mutate(ids);
  }

  return (
    <main className="page page--narrow">
      <PageHeader
        title="Medios de pago"
        backHref="/more"
        actions={<Button variant="primary" onClick={() => setEditing("new")}><PlusIcon /> Nuevo</Button>}
      />
      <section className="page-section">
        <div className="section-intro">
          <div>
            <p className="eyebrow">Cobros</p>
            <h1>Medios de pago</h1>
            <p>Definí el descuento automático y cómo se registra cada cobro.</p>
          </div>
        </div>

        {methods.isLoading && <div className="skeleton-list"><span /><span /><span /></div>}
        <div className="entity-list">
          {methods.data?.map((method, index, list) => {
            // El backend siempre ordena activos primero y luego inactivos
            // (active desc). Un vecino con distinto `active` marca el borde
            // del grupo: cruzarlo haría que la fila "salte" sola al lado
            // opuesto en el próximo GET, así que ahí deshabilitamos la flecha.
            const prev = list[index - 1];
            const next = list[index + 1];
            const canMoveUp = index > 0 && prev.active === method.active;
            const canMoveDown = index < list.length - 1 && next.active === method.active;
            return (
              <article className={`entity-row payment-method-row${method.active ? "" : " is-inactive"}`} key={method.id}>
                <span className={`entity-row__icon payment-method-icon payment-${method.color}`}>
                  <MethodGlyph method={method} />
                </span>
                <div className="entity-row__body">
                  <strong>{method.name}</strong>
                  <small>
                    {method.discountPct > 0 ? `${formatPct(method.discountPct)}% de descuento automático` : "Sin descuento"}
                    {method.requiresNetReceived ? " · pide neto" : ""}
                    {method.requiresCustomer ? " · pide cliente" : ""}
                    {method.settlesInBatch ? " · por liquidación" : ""}
                  </small>
                </div>
                <span className={`status-badge ${method.active ? "status-badge--success" : "status-badge--neutral"}`}>{method.active ? "Activo" : "Inactivo"}</span>
                <button className="icon-action" type="button" aria-label={`Editar ${method.name}`} onClick={() => setEditing(method)}><PencilIcon /></button>
                <div className="payment-method-row__controls">
                  <div className="payment-method-order-buttons">
                    <button
                      className="icon-action payment-method-order-btn"
                      type="button"
                      aria-label={`Subir ${method.name}`}
                      disabled={!canMoveUp || reorder.isPending}
                      onClick={() => moveMethod(index, -1)}
                    >
                      <ArrowUpIcon />
                    </button>
                    <button
                      className="icon-action payment-method-order-btn"
                      type="button"
                      aria-label={`Bajar ${method.name}`}
                      disabled={!canMoveDown || reorder.isPending}
                      onClick={() => moveMethod(index, 1)}
                    >
                      <ArrowDownIcon />
                    </button>
                  </div>
                  <button
                    className="text-action"
                    type="button"
                    disabled={update.isPending}
                    onClick={async () => {
                      await update.mutateAsync({ id: method.id, body: { active: !method.active } });
                      setFeedback(method.active ? "Medio de pago desactivado" : "Medio de pago activado");
                    }}
                  >
                    {method.active ? "Desactivar" : "Activar"}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <PaymentMethodSheet
        target={editing}
        nextDisplayOrder={nextDisplayOrder}
        onClose={() => setEditing(null)}
        onSaved={(message) => {
          setEditing(null);
          setFeedback(message);
        }}
      />
      {feedback && <Toast kind="success" message={feedback} icon={<CheckIcon />} autoCloseMs={2400} onDismiss={() => setFeedback(null)} />}
    </main>
  );
}

function PaymentMethodSheet({ target, nextDisplayOrder, onClose, onSaved }: { target: Target; nextDisplayOrder: number; onClose: () => void; onSaved: (message: string) => void }) {
  const create = useCreatePaymentMethod();
  const update = useUpdatePaymentMethod();
  const [name, setName] = useState("");
  const [color, setColor] = useState<PaymentMethodInput["color"]>("other");
  const [icon, setIcon] = useState<PaymentMethodInput["icon"]>("Banknote");
  const [discount, setDiscount] = useState("0");
  const [requiresNetReceived, setRequiresNetReceived] = useState(false);
  const [requiresCustomer, setRequiresCustomer] = useState(false);
  const [settlesInBatch, setSettlesInBatch] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const method = target && target !== "new" ? target : null;
    setName(method?.name ?? "");
    setColor((method?.color as PaymentMethodInput["color"] | undefined) ?? "other");
    setIcon((method?.icon as PaymentMethodInput["icon"] | undefined) ?? "Banknote");
    setDiscount(method ? formatPct(method.discountPct) : "0");
    setRequiresNetReceived(method?.requiresNetReceived ?? false);
    setRequiresCustomer(method?.requiresCustomer ?? false);
    setSettlesInBatch(method?.settlesInBatch ?? false);
    setError(null);
  }, [target]);

  if (!target) return null;
  const saving = create.isPending || update.isPending;

  return (
    <Sheet open onClose={onClose} title={target === "new" ? "Nuevo medio de pago" : "Editar medio de pago"}>
      <form className="form-stack" onSubmit={async (event) => {
        event.preventDefault();
        const discountPct = Number(discount.replace(",", "."));
        if (name.trim().length < 2) return setError("Ingresá un nombre de al menos 2 caracteres.");
        if (!Number.isFinite(discountPct) || discountPct < 0 || discountPct > 100 || Math.round(discountPct * 10) !== discountPct * 10) {
          return setError("El descuento debe estar entre 0 y 100, con hasta un decimal.");
        }
        const fields = {
          name: name.trim(), color, icon, discountPct,
          requiresNetReceived, requiresCustomer, settlesInBatch,
        };
        try {
          // Al crear, el orden se asigna solo (va al final). Al editar, se
          // omite displayOrder: el PATCH usa un schema parcial y omitirlo
          // deja el valor existente sin tocar, en vez de contradecir a las
          // flechas de la lista.
          if (target === "new") await create.mutateAsync({ ...fields, displayOrder: nextDisplayOrder });
          else await update.mutateAsync({ id: target.id, body: fields });
          onSaved(target === "new" ? "Medio de pago creado" : "Medio de pago actualizado");
        } catch {
          setError("Ya existe un medio con ese nombre o no se pudo guardar.");
        }
      }}>
        <Input label="Nombre" value={name} onChange={(event) => { setName(event.target.value); setError(null); }} autoFocus />
        <div className="payment-method-form-grid">
          <label className="field-control"><span>Color</span><select value={color} onChange={(event) => setColor(event.target.value as PaymentMethodInput["color"])}>{colorOptions.map((option) => <option value={option.value} key={option.value}>{option.label}</option>)}</select></label>
          <label className="field-control"><span>Ícono</span><select value={icon} onChange={(event) => setIcon(event.target.value as PaymentMethodInput["icon"])}>{iconOptions.map((option) => <option value={option.value} key={option.value}>{option.label}</option>)}</select></label>
          <Input label="Descuento %" value={discount} onChange={(event) => { setDiscount(event.target.value.replace(/[^\d,.]/g, "")); setError(null); }} inputMode="decimal" />
        </div>
        <label className={`switch-row payment-method-switch${settlesInBatch ? " is-disabled" : ""}`}>
          <input
            type="checkbox"
            checked={requiresNetReceived}
            disabled={settlesInBatch}
            onChange={(event) => {
              const checked = event.target.checked;
              setRequiresNetReceived(checked);
              if (checked) setSettlesInBatch(false);
            }}
          />
          <span><strong>Pedir neto recibido</strong><small>Para tarjetas o plataformas que descuentan comisión.</small></span>
        </label>
        <label className={`switch-row payment-method-switch${settlesInBatch ? " is-disabled" : ""}`}>
          <input
            type="checkbox"
            checked={requiresCustomer}
            disabled={settlesInBatch}
            onChange={(event) => {
              const checked = event.target.checked;
              setRequiresCustomer(checked);
              if (checked) setSettlesInBatch(false);
            }}
          />
          <span><strong>Pedir cliente</strong><small>Para cuenta corriente u otros cobros asociados a una persona.</small></span>
        </label>
        <label className={`switch-row payment-method-switch${requiresNetReceived || requiresCustomer ? " is-disabled" : ""}`}>
          <input
            type="checkbox"
            checked={settlesInBatch}
            disabled={requiresNetReceived || requiresCustomer}
            onChange={(event) => {
              const checked = event.target.checked;
              setSettlesInBatch(checked);
              if (checked) {
                setRequiresNetReceived(false);
                setRequiresCustomer(false);
              }
            }}
          />
          <span><strong>Se cobra por liquidación</strong><small>La plata llega días después, consolidada. No se pide neto en la venta.</small></span>
        </label>
        {error && <p className="form-error">{error}</p>}
        <p className="field-help">Con 0% se quita el descuento. Con un porcentaje mayor, se aplica automáticamente solo a productos sin descuento propio.</p>
        <Button type="submit" fullWidth size="lg" disabled={saving}>{saving ? "Guardando…" : "Guardar medio de pago"}</Button>
      </form>
    </Sheet>
  );
}

function MethodGlyph({ method }: { method: Pick<PaymentMethod, "icon" | "color"> }) {
  if (method.icon === "ArrowLeftRight") return <ArrowLeftRightIcon />;
  if (method.icon === "CreditCard") return <CreditCardIcon />;
  if (method.icon === "UserCircle") return <UserIcon />;
  return <BanknoteIcon />;
}

function formatPct(value: number) {
  return value.toLocaleString("es-AR", { maximumFractionDigits: 1 });
}
